import { motion, AnimatePresence } from "motion/react";
import { Phone, Video, Mic, X, Globe, Star } from "lucide-react";
import React, { useState } from "react";

interface IncomingCallModalProps {
  incomingCall: {
    sessionId: string | number;
    callerName: string;
    emotion: string;
    method?: "video" | "audio";
    skills?: string[];
    languages?: string[];
  };
  onAccept: () => void;
  onReject: () => void;
}

export function IncomingCallModal({ incomingCall, onAccept, onReject }: IncomingCallModalProps) {
  const [isAccepting, setIsAccepting] = useState(false);

  const handleAccept = () => {
    setIsAccepting(true);
    setTimeout(() => onAccept(), 300);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-md z-[300] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.85, y: 30, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="relative bg-white rounded-[36px] shadow-2xl max-w-sm w-full overflow-hidden"
        >
          {/* Gradient Header */}
          <div className="relative h-40 bg-gradient-to-br from-purple-600 via-violet-600 to-pink-500 overflow-hidden flex items-center justify-center">
            {/* Animated rings */}
            <motion.div
              animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.1, 0.3] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="absolute w-56 h-56 rounded-full border-2 border-white/30"
            />
            <motion.div
              animate={{ scale: [1, 1.6, 1], opacity: [0.2, 0.05, 0.2] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
              className="absolute w-56 h-56 rounded-full border border-white/20"
            />

            {/* Avatar */}
            <div className="relative z-10">
              <div className="w-20 h-20 rounded-full border-4 border-white shadow-xl overflow-hidden bg-white">
                <img
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${incomingCall.callerName}`}
                  alt={incomingCall.callerName}
                  className="w-full h-full"
                />
              </div>
              {/* Method badge */}
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className={`absolute -bottom-2 -right-2 w-9 h-9 rounded-full border-3 border-white flex items-center justify-center shadow-lg ${
                  incomingCall.method === "audio"
                    ? "bg-indigo-500"
                    : "bg-emerald-500"
                }`}
              >
                {incomingCall.method === "audio" ? (
                  <Mic className="w-4 h-4 text-white" />
                ) : (
                  <Video className="w-4 h-4 text-white" />
                )}
              </motion.div>
            </div>



            {/* Decline X (top-left) */}
            <button
              onClick={onReject}
              className="absolute top-4 left-4 w-9 h-9 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 pt-5 pb-6">
            {/* Caller Info */}
            <div className="text-center mb-4">
              <h3 className="text-2xl font-black text-gray-900 leading-tight">
                {incomingCall.callerName}
              </h3>
              <p className="text-gray-500 text-sm font-medium mt-1">
                Needs{" "}
                <span className="text-purple-600 font-bold">
                  {incomingCall.emotion}
                </span>{" "}
                support via{" "}
                <span className="text-gray-800 font-bold capitalize">
                  {incomingCall.method || "video"}
                </span>
              </p>
            </div>

            {/* Skills / Languages chips */}
            {((incomingCall.skills && incomingCall.skills.length > 0) ||
              (incomingCall.languages && incomingCall.languages.length > 0)) && (
              <div className="bg-purple-50 rounded-2xl px-4 py-3 mb-5 space-y-2">
                {incomingCall.skills && incomingCall.skills.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 text-purple-600">
                      <Star className="w-3.5 h-3.5" />
                      <span className="text-xs font-bold uppercase tracking-wider">Skills</span>
                    </div>
                    {incomingCall.skills.slice(0, 3).map((skill) => (
                      <span
                        key={skill}
                        className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
                {incomingCall.languages && incomingCall.languages.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 text-indigo-600">
                      <Globe className="w-3.5 h-3.5" />
                      <span className="text-xs font-bold uppercase tracking-wider">Languages</span>
                    </div>
                    {incomingCall.languages.slice(0, 3).map((lang) => (
                      <span
                        key={lang}
                        className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pulse indicator */}
            <div className="flex items-center justify-center gap-2 mb-5">
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="w-2.5 h-2.5 bg-green-500 rounded-full"
              />
              <span className="text-sm font-semibold text-gray-500">
                Incoming call waiting…
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onReject}
                className="flex-1 py-4 bg-red-50 text-red-500 rounded-2xl font-bold hover:bg-red-100 transition-colors text-sm"
              >
                Decline
              </button>
              <motion.button
                onClick={handleAccept}
                disabled={isAccepting}
                whileTap={{ scale: 0.97 }}
                className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl font-bold shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isAccepting ? (
                  <span className="animate-pulse">Connecting…</span>
                ) : (
                  <>
                    <Phone className="w-4 h-4 fill-current" />
                    Accept
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
